import {
    analyzeRoute,
    ROUTE_SOFT_LIMITS,
    RoutePoint,
    RouteWarningsResult,
} from '@escapenavigator/utils/dist';
import { Alert, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconInfoCircle, IconRoute, IconWalk } from '@tabler/icons-react';
import { useMemo } from 'react';

import { t } from '../dictionary';

type Props = {
    points: RoutePoint[];
    stepCount: number;
    hasUnapproved: boolean;
};

export const useRouteAnalysis = (points: RoutePoint[]): RouteWarningsResult | null =>
    useMemo(() => (points.length >= 2 ? analyzeRoute(points) : null), [points]);

export const RouteWarnings = ({ points, stepCount, hasUnapproved }: Props) => {
    const analysis = useRouteAnalysis(points);

    const softSteps =
        stepCount > 0 &&
        (stepCount < ROUTE_SOFT_LIMITS.minSteps || stepCount > ROUTE_SOFT_LIMITS.maxSteps);

    const softDistance =
        !!analysis &&
        (analysis.totalDistanceKm < ROUTE_SOFT_LIMITS.minTotalKm ||
            analysis.totalDistanceKm > ROUTE_SOFT_LIMITS.maxTotalKm);

    if (!hasUnapproved && !analysis && !softSteps) return null;

    return (
        <Stack gap='sm'>
            {hasUnapproved && (
                <Alert variant='light' color='red' icon={<IconAlertTriangle />}>
                    {t.stories.unapprovedBlockSubmit}
                </Alert>
            )}

            {analysis && (
                <Alert variant='light' color='indigo' icon={<IconWalk />}>
                    <Text size='sm' fw={600}>
                        {t.stories.walkingTime(analysis.estimatedMinutes)}
                    </Text>
                    <Text size='sm' c='dimmed'>
                        {t.stories.routeSummary(stepCount, analysis.totalDistanceKm.toFixed(1))}
                    </Text>
                </Alert>
            )}

            {analysis?.neighborWarnings.map((w) => (
                <Alert
                    key={`${w.fromIndex}-${w.toIndex}`}
                    variant='light'
                    color={w.level === 'red' ? 'red' : 'yellow'}
                    icon={<IconAlertTriangle />}
                >
                    {w.level === 'red'
                        ? t.stories.neighborFarRed(
                              w.fromIndex + 1,
                              w.toIndex + 1,
                              w.distanceKm.toFixed(1),
                          )
                        : t.stories.neighborFarYellow(
                              w.fromIndex + 1,
                              w.toIndex + 1,
                              w.distanceKm.toFixed(1),
                          )}
                </Alert>
            ))}

            {analysis?.sequenceWarning && (
                <Alert variant='light' color='yellow' icon={<IconRoute />}>
                    {t.stories.sequenceWarning}
                </Alert>
            )}

            {softSteps && (
                <Alert variant='light' color='gray' icon={<IconInfoCircle />}>
                    {t.stories.softStepsWarning(stepCount)}
                </Alert>
            )}

            {softDistance && analysis && (
                <Alert variant='light' color='gray' icon={<IconInfoCircle />}>
                    {t.stories.softDistanceWarning(analysis.totalDistanceKm.toFixed(1))}
                </Alert>
            )}
        </Stack>
    );
};
